import { FC, useEffect, useRef } from 'react';
import { Input, Flex, FormControl } from '@eqi-investimentos/react';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
} from '@chakra-ui/react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { object, string } from 'yup';
import { sanitizeNumber } from '@/utils/format';
import { formatMonetary } from '@/utils/masks';

export const ModalEditSliderValue: FC<ModalEditSliderValueProps> = ({
  isOpen,
  value,
  onChangeSlideValue,
  onClose,
  options,
}) => {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const resolver = useRef<(result: boolean) => void>(null);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm({
    resolver: yupResolver(
      object().shape({
        input: string(),
      })
    ),
    reValidateMode: 'onChange',
    mode: 'onSubmit',
  });

  useEffect(() => {
    if (isOpen) {
      const newValue = options.input.isCurrency
        ? formatMonetary(`${parseFloat(value) * 100}`)
        : value;
      reset({ input: newValue });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, value, reset]);

  const handleClose = () => {
    onClose();
    resolver.current?.(false);
  };

  const onSubmit = (values: { input?: string }) => {
    const numberParsed = parseFloat(sanitizeNumber(values.input || '0'));
    const formatted = options.input.isCurrency
      ? numberParsed / 100
      : numberParsed;
    onChangeSlideValue(formatted);
    handleClose();
  };

  if (!isOpen) return <></>;

  const watchedInput = watch('input');
  const isOverMax = (() => {
    if (!options.blockMaxLimits || options.input.max === undefined)
      return false;
    const raw = parseFloat(sanitizeNumber(watchedInput || '0'));
    const actual = options.input.isCurrency ? raw / 100 : raw;
    return !isNaN(actual) && actual > options.input.max;
  })();

  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={handleClose}
      isCentered
    >
      <AlertDialogOverlay>
        <AlertDialogContent
          w="23.4375rem"
          h="fit-content"
          gap={4}
          py="2xl"
          px="l"
        >
          {options.title && (
            <AlertDialogHeader p={0}>{options.title}</AlertDialogHeader>
          )}
          <AlertDialogBody fontSize="sm" lineHeight={6}>
            <Flex
              as="form"
              direction="column"
              gap="l"
              onSubmit={handleSubmit(onSubmit)}
            >
              <FormControl
                errorMessage={errors.input?.message}
                size="lg"
                isRequired
              >
                <Controller
                  name={'input'}
                  control={control}
                  render={({ field }) => (
                    <Input
                      type="text"
                      h="3rem"
                      name="input"
                      placeholder={options.input.placeholder ?? ''}
                      value={field.value}
                      min={options.input.min}
                      max={options.input.max}
                      onChange={({ target: { value } }) => {
                        const newValue = options.input.isCurrency
                          ? formatMonetary(value)
                          : value;
                        field.onChange(newValue);
                      }}
                      borderRadius={6}
                      px="xs"
                      border="1px solid"
                      borderColor="border.secondary"
                    />
                  )}
                />
              </FormControl>

              <Flex direction="column" gap="m">
                <Button
                  type="submit"
                  w="full"
                  size="md"
                  justifyContent="center"
                  isDisabled={isOverMax}
                >
                  {options.confirmText || 'Salvar'}
                </Button>
                <Button
                  variant="outline"
                  ref={cancelRef}
                  onClick={handleClose}
                  w="full"
                  size="md"
                  justifyContent="center"
                >
                  {options.cancelText || 'Cancelar'}
                </Button>
              </Flex>
            </Flex>
          </AlertDialogBody>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};

export type ConfirmOptions = {
  title?: string;
  blockMaxLimits?: boolean;
  input: {
    min?: number;
    max?: number;
    isCurrency?: boolean;
    placeholder?: string;
  };
  confirmText?: string;
  cancelText?: string;
};

type ModalEditSliderValueProps = {
  isOpen: boolean;
  value: string;
  onChangeSlideValue: (value: number) => void;
  onClose: () => void;
  options: ConfirmOptions;
};
